import React from "react";
import { withStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Select from "@material-ui/core/Select";

import ConfirmationWrapper from "../components/confirmation_wrapper";

const styles = (theme) => ({
  container: {
    display: "inline-block",
    flexDirection: "row",
    minWidth: "100%",
  },
  formControl: {
    minWidth: "100%",
  },
  menuItemIcon: {
    color: theme.palette.primary.main,
    fontSize: "1.2em", // disabled Select height == Select height.
    paddingRight: "15px",
    paddingLeft: "5px",
    float: "right",
  },
  menuItemEditableIndicator: {
    display: "block",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      "& $menuItemIcon": {
        color: "white",
      },
    },
  },
});

const styleOverrideHalf = {
  minWidth: "50%",
};

const unassignedValue = null;

/********************************************
A Select is a dropdown of list items.
The list of values prop should be a list of tuples
of (db value, the 'value' to display).

Pseudo-stateless, state simply passes through this
component up to the parent event handler.

@prop: confirmed := (Optional) an array of [bool, str, str] of;
        [add a confirmation onto this component?, confirmation title, confirmation message].
        e.g. [True, "Title", "Message].
        otherwise; [False, "", ""], [False], or False.
@prop: confirmSpecificStates := (Optional) a list of contentBuffer states to confirm on.
         if undefined ==> confirm all states (iff `confirmed` is defined).
         e.g. ["1", "2", ]
*********************************************/
class SimpleSelect extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      contentBuffer: props.value, // this is for the component state for bubbling up to parent.
      confirmationBufferProposed: unassignedValue, // confirmation uses intermediate buffer to store the value until the "Confirm" button is pressed.
    };
  }

  handleChange(event) {
    if (this.state.contentBuffer === event.target.value) {
      return; // i.e. no change.
    }

    if (this.usingConfirmations()) {
      // Save the value into the confirmation buffer, then wait for the "Confirm" or "Cancel".
      this.setState({ confirmationBufferProposed: event.target.value });
      return;
    }

    this.bubbleStateUp(event.target.value);
  }

  // i) A select without confirmation will trigger this function,
  // ii) Otherwise, the Confirmation dialog "Confirm" button press triggers this function.
  bubbleStateUp(newContentBuffer) {
    if (this.usingConfirmations()) {
      if (this.state.confirmationBufferProposed === unassignedValue) {
        return; // e.g) hack to prevent clicking on current/same value and then "Confirm".
      }
      newContentBuffer = this.state.confirmationBufferProposed;
    }

    this.setState(
      {
        contentBuffer: newContentBuffer,
        confirmationBufferProposed: unassignedValue,
      },
      function () {
        this.props.onChange(
          this.props.url,
          this.props.attributeId,
          this.state.contentBuffer.toString()
        );
      }
    );
  }

  clearConfirmationBuffer() {
    this.setState({ confirmationBufferProposed: unassignedValue });
  }

  usingConfirmations() {
    return (
      this.props.confirmed && this.props.confirmed[0] && this.confirmStates()
    );
  }

  confirmStates() {
    if (this.props.confirmSpecificStates === undefined) {
      return true; // Optional param. Default: confirmation on all contentBuffer states.
    }

    return this.props.confirmSpecificStates.includes(this.state.contentBuffer);
  }

  renderSelect(classes) {
    const labelLowerCase = this.props.label.toLowerCase();
    return (
      <FormControl
        variant="outlined"
        className={`select ${classes.formControl}`}
        component={Paper}
        elevation={2}
      >
        <InputLabel id={"select-" + labelLowerCase + "-label"}>
          {this.props.label}
        </InputLabel>
        <Select
          labelId={"select-" + labelLowerCase + "-label"}
          id={"select-" + labelLowerCase}
          value={this.state.contentBuffer}
          name={this.props.name}
          label={this.props.label}
          url={this.props.url}
          disabled={!this.props.editable}
          onChange={this.handleChange.bind(this)}
        >
          {this.props.listValues.map((itemValue) => (
            <MenuItem
              key={this.props.name + "-" + itemValue[0]}
              value={itemValue[0]}
              className={classes.menuItemEditableIndicator}
              dense={true}
            >
              {itemValue[1]}
              {this.props.editable ? (
                <EditIcon className={classes.menuItemIcon} />
              ) : null}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  render() {
    const { classes } = this.props;
    const widthStyleOverride =
      this.props.width === "half" ? styleOverrideHalf : null;

    // Trigger a value change when data coming from server is different than displayed.
    // e.g. Division or Subdivision select dropdown component should change from
    // other input component, 'Total League Points'.
    if (
      this.state.contentBuffer !== this.props.value &&
      this.props.updateByOtherFields === true
    ) {
      this.setState({ contentBuffer: this.props.value });
    }

    return (
      <div
        key={this.props.key}
        className={classes.container}
        style={widthStyleOverride}
      >
        <ConfirmationWrapper
          confirmClick={this.bubbleStateUp.bind(this)}
          cancelClick={this.clearConfirmationBuffer.bind(this)}
          disableConfirmations={
            !this.props.editable || !this.usingConfirmations()
          }
          title={this.props.confirmed ? this.props.confirmed[1] : ""}
          confirmationMessage={
            this.props.confirmed ? this.props.confirmed[2] : ""
          }
        >
          {this.renderSelect.bind(this)(classes)}
        </ConfirmationWrapper>
      </div>
    );
  }
}

export default withStyles(styles)(SimpleSelect);
